import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEye,
  faEdit,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faToggleOn,
  faToggleOff,
  faBars,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import BildirimSablonuModal from '../components/BildirimSablonuModal';
import { useSnackbar } from 'notistack';

function BildirimSablonlari() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage, setTemplatesPerPage] = useState(10);

  // Calculate current templates
  const indexOfLastTemplate = currentPage * templatesPerPage;
  const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
  const currentTemplates = filteredTemplates.slice(
    indexOfFirstTemplate,
    indexOfLastTemplate
  );

  // Calculate total pages
  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle templates per page change
  const handleTemplatesPerPageChange = (e) => {
    setTemplatesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Set filtered templates whenever templates change
  useEffect(() => {
    setFilteredTemplates(templates);
  }, [templates]);

  // Reset to first page when templatesPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [templatesPerPage]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);

      // First, get the count
      const { count, error: countError } = await supabase
        .from('notification_templates')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        throw countError;
      }

      // Then get the actual data
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setTotalTemplates(count);
      setTemplates(data || []);
      setFilteredTemplates(data || []);
    } catch (error) {
      console.error('Error fetching notification templates:', error);
      setError('Bildirim şablonları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (value.trim() === '') {
      setFilteredTemplates(templates);
    } else {
      const filtered = templates.filter(
        (template) =>
          template.title.toLowerCase().includes(value) ||
          template.body.toLowerCase().includes(value) ||
          (template.type && template.type.toLowerCase().includes(value))
      );
      setFilteredTemplates(filtered);
    }

    setCurrentPage(1);
  };

  const openTemplateModal = (template = null) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleSaveTemplate = () => {
    fetchTemplates();
    enqueueSnackbar('Bildirim şablonu başarıyla kaydedildi', {
      variant: 'success',
    });
  };

  const handleDeleteTemplate = async (id) => {
    if (
      window.confirm(
        'Bu bildirim şablonunu silmek istediğinizden emin misiniz?'
      )
    ) {
      try {
        const { error } = await supabase
          .from('notification_templates')
          .delete()
          .eq('id', id);

        if (error) throw error;

        fetchTemplates();
        enqueueSnackbar('Bildirim şablonu başarıyla silindi', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error deleting notification template:', error);
        enqueueSnackbar('Bildirim şablonu silinirken bir hata oluştu', {
          variant: 'error',
        });
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('notification_templates')
        .update({ active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      fetchTemplates();
      enqueueSnackbar('Bildirim şablonu durumu güncellendi', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating notification template status:', error);
      enqueueSnackbar('Durum güncellenirken bir hata oluştu', {
        variant: 'error',
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Bildirim Şablonları
              </h1>
              <p className="text-sm text-gray-500">
                Toplam Şablon: {totalTemplates}
              </p>
            </div>
            <button
              onClick={() => openTemplateModal()}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Şablon Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şablon Ara
                </label>
                <input
                  type="text"
                  placeholder="Şablon ara..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Templates Table */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-4 text-center">Yükleniyor...</div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">{error}</div>
            ) : filteredTemplates.length === 0 ? (
              <div className="p-4 text-center">Bildirim şablonu bulunamadı</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Başlık
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İçerik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tür
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Eylemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTemplates.map((template) => (
                      <tr key={template.id}>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {template.title}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {template.body.length > 50
                              ? `${template.body.substring(0, 50)}...`
                              : template.body}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {template.type || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <button
                              onClick={() =>
                                handleToggleActive(template.id, template.active)
                              }
                              className={`text-${template.active ? 'green' : 'gray'}-600 hover:text-${
                                template.active ? 'green' : 'gray'
                              }-900`}
                              title={template.active ? 'Aktif' : 'Pasif'}
                            >
                              <FontAwesomeIcon
                                icon={
                                  template.active ? faToggleOn : faToggleOff
                                }
                                size="lg"
                              />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => openTemplateModal(template)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Düzenle"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Sil"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Önceki
                  </button>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={templatesPerPage}
                      onChange={handleTemplatesPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Sonraki
                  </button>
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 mr-4">
                    Görüntülenen{' '}
                    <span className="font-medium">
                      {indexOfFirstTemplate + 1}
                    </span>{' '}
                    -{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastTemplate, filteredTemplates.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">
                      {filteredTemplates.length}
                    </span>{' '}
                    sonuç
                  </p>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={templatesPerPage}
                      onChange={handleTemplatesPerPageChange}
                      className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="h-4 w-4"
                      />
                    </button>

                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show current page, first page, last page, and one page before and after current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      // Show dots if there's a gap
                      if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="h-4 w-4"
                      />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Add the modal */}
      <BildirimSablonuModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        template={selectedTemplate}
        onSave={handleSaveTemplate}
      />
    </div>
  );
}

export default BildirimSablonlari;
